import {ErrorHandler, Injectable} from '@angular/core';
import {ToasterService} from '@shared/components/toaster/toaster.service';
import {Toast, ToastType} from '@shared/components/toaster/Toast';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {HttpErrorResponse} from '@angular/common/http';
import {BaseService} from '@shared/services/api/base-service';
import {I18nMessage} from '@shared/api/model/i18n';
import moment from 'moment-timezone';
import {UsageAnalyticsService} from "@shared/services/usage-analytics/usage-analytics.service";
import {ConfigService} from "@shared/services/config/config.service";


export class I18nError extends Error {
  parameters: { [key: string]: any };

  constructor(message: string, parameters?: { [key: string]: any }) {
    super(message);
    this.parameters = parameters;
  }
}


interface ServiceErrorResponse {
  errorCode: number;
  service: string;
  path: string;
  correlationId: string;
  timestamp: string;
}


@Injectable({
  providedIn: 'root'
})
export class CustomErrorHandler implements ErrorHandler {

  private readonly logger: AceLogger;
  private readonly correlationIdHeaderRegex = /x-(.+?)-correlation-id/i;
  private readonly USER_ERROR_EVENT_NAME = 'UserError';

  constructor(private toasterService: ToasterService,
              private usageAnalyticsService: UsageAnalyticsService,
              private configService: ConfigService,
              logService: AceLoggerService) {
    this.logger = logService.create(CustomErrorHandler.name);
  }

  handleError(error: any) {
    if (!error) {
      error = new I18nError('An unexpected error occurred');
    } else if (error.promise && error.rejection) {
      /* Unwrap the error if wrapped for uncaught promise rejection by zone.js
         Consider disabling this wrapping completely https://github.com/angular/angular/commit/8456c5ec60a3fb424c569b36bf4705afc5fc9597*/
      error = error.rejection;
    }

    this.logger.error(error);
    if (error.cause) {
      /* Currently when Chrome logs an error object, it does not display it's "cause" if present. https://issues.chromium.org/issues/40182832
      It can however explicitly log error.cause */
      this.logger.error('Error cause: ', error.cause);
    }

    let text: string = null;
    let message: I18nMessage = null;
    let errorCode = null;

    if (error instanceof I18nError) {
      message = {messageKey: error.message, parameters: error.parameters};
    } else {
      if (error instanceof HttpErrorResponse) {
        const serviceError = this.getServiceError(error);
        if (serviceError) {
          // eslint-disable-next-line max-len
          text = `Service error occurred. Path: ${serviceError.path}, Service: ${serviceError.service}, Status Code: ${serviceError.errorCode}, Error Id: ${serviceError.correlationId}, Timestamp: ${serviceError.timestamp}.`;
        } else if (error.error && error.error[BaseService.ERRORS_PREFIX]) {
          text = JSON.stringify(error.error[BaseService.ERRORS_PREFIX]);
        } else {
          text = error.message;
        }
        errorCode = error.status;
      } else {
        if (error.message) {
          text = error.message + (error.cause ? '; Cause: ' + error.cause : '');
        } else {
          text = JSON.stringify(error);
        }
      }
    }
    this.trackError(errorCode, message ?? text);
    const toast: Toast = {text, message, type: ToastType.Danger, dismissible: true, timeout: 0, error: true};
    this.toasterService.push(toast);
  }

  private trackError(errorCode: number | null, errorMessage: I18nMessage | string) {
    const config = this.configService.getCachedConfig();
    if (config.trackError) {
      this.usageAnalyticsService.trackAndIncludeUserEmail(this.USER_ERROR_EVENT_NAME, {
        ...errorCode != null && {error_status_code: errorCode},
        error: errorMessage
      });
    }
  }

  private getServiceError(error: HttpErrorResponse): ServiceErrorResponse | null {
    const headers = error.headers;
    if (headers) {
      for (let header of headers.keys()) {
        const correlationMatch = header.match(this.correlationIdHeaderRegex);
        if (correlationMatch?.length > 1) {
          return {
            errorCode: error.status,
            service: correlationMatch[1],
            path: error.url ? new URL(error.url).pathname : null,
            correlationId: headers.get(header),
            timestamp: moment.utc().toISOString()
          };
        }
      }
    } else {
      return null;
    }
  }
}
